/* eslint-disable */
// BillingSettings.js
// Import and add <BillingSettings /> as first section in Settings.js

import { API_URL } from '../../config';
import React, { useState } from 'react';
import { useBilling } from '../billing/BillingContext';
import { UpgradeModal } from '../billing/UpgradePrompts';
import { useAuth } from '../../context/AuthContext';

const PLAN_COLOURS = { free: '#6b7280', solo: '#0891b2', standard: '#2563eb', pro: '#7c3aed', agency: '#065f46' };
const HARD_CAPPED_PLANS = ['free', 'solo'];

export function BillingSettings() {
    const { billing, refetch }              = useBilling();
    const { isOwner }                       = useAuth();
    const [showUpgrade, setShowUpgrade]     = useState(false);
    const [portalLoading, setPortalLoading] = useState(false);
    const [portalError, setPortalError]     = useState('');

    if (!billing) return <div style={{ padding: '1rem', color: '#6b7280' }}>Loading billing info...</div>;

    const plan      = billing.plan || 'free';
    const colour    = PLAN_COLOURS[plan] || '#6b7280';
    const periodEnd = billing.currentPeriodEnd ? new Date(billing.currentPeriodEnd).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
    const cancelAt  = billing.cancelAt ? new Date(billing.cancelAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : (periodEnd || null);
    const trialEnd  = billing.trialEndsAt     ? new Date(billing.trialEndsAt).toLocaleDateString('en-GB',      { day: 'numeric', month: 'long', year: 'numeric' }) : null;

    const openPortal = async () => {
        setPortalLoading(true); setPortalError('');
        try {
            const BASE  = API_URL;
            const token = localStorage.getItem('token');
            const res   = await fetch(`${BASE}/api/billing/create-portal-session`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
            const data  = await res.json();
            if (!res.ok) throw new Error(data.error);
            window.location.href = data.url;
        } catch (e) { setPortalError(e.message || 'Could not open billing portal.'); setPortalLoading(false); }
    };



    return (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '1.25rem' }}>💳 Billing & Plan</h3>

            {/* Current plan banner */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: `${colour}12`, border: `1px solid ${colour}30`, borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div>
                    <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: '0.125rem' }}>Current plan</div>
                    <div style={{ fontWeight: 700, fontSize: '1.125rem', color: colour, textTransform: 'capitalize' }}>
                        casavello {billing.planName || 'Free'}
                        {billing.billingInterval === 'year' && <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#059669', marginLeft: '0.5rem' }}>Annual</span>}
                    </div>
                    {billing.planStatus === 'trialing' && trialEnd && <div style={{ fontSize: '0.8125rem', color: '#d97706', marginTop: '0.125rem' }}>Trial ends {trialEnd}</div>}
                    {billing.planStatus === 'past_due'  && <div style={{ fontSize: '0.8125rem', color: '#dc2626', fontWeight: 600, marginTop: '0.125rem' }}>⚠️ Payment failed — please update your payment method</div>}
                    {periodEnd && billing.planStatus === 'active' && !billing.cancelPending && <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: '0.125rem' }}>Renews {periodEnd}</div>}
                    {billing.cancelPending && (
                        <div style={{ fontSize: '0.8125rem', color: '#b45309', marginTop: '0.35rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '0.5rem', padding: '0.5rem 0.75rem' }}>
                            ⏳ Your {billing.planName} plan is active until <strong>{cancelAt}</strong>. After this you'll move to the Free plan (5 properties, 1 user). You can resubscribe anytime before then via Manage Billing.
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {!isOwner() ? (
                        <div style={{ fontSize: '0.8125rem', color: '#6b7280', fontStyle: 'italic' }}>Only the organisation owner can manage billing</div>
                    ) : plan === 'free' ? (
                        <button onClick={() => setShowUpgrade(true)} style={{ background: colour, color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' }}>Upgrade Plan</button>
                    ) : (
                        <>
                            <button onClick={() => setShowUpgrade(true)} style={{ background: colour, color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' }}>Change Plan</button>
                            <button onClick={openPortal} disabled={portalLoading} style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '0.5rem', padding: '0.5rem 1rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
                                {portalLoading ? 'Loading...' : 'Manage Billing'}
                            </button>
                        </>
                    )}
                </div>
            </div>
            {portalError && <div style={{ color: '#dc2626', fontSize: '0.875rem', marginBottom: '1rem' }}>{portalError}</div>}
            {isOwner() && plan !== 'free' && (
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '1rem' }}>
                    ℹ️ If you cancel, you'll have 30 days after cancellation to export your data (Settings → Data Export) before it's permanently deleted — see our <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#6b7280' }}>Terms of Service</a> for details.
                </div>
            )}

            {/* Usage stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ background: '#f9fafb', borderRadius: '0.5rem', padding: '0.875rem 1rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Let units</div>
                    <div style={{ fontWeight: 700, fontSize: '1.125rem', color: '#111' }}>
                        {billing.billableUnits} / {billing.includedUnits > 999 ? '∞' : billing.includedUnits}
                    </div>
                    {billing.atUnitLimit && (
                        <div style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600, marginTop: '0.125rem' }}>Limit reached</div>
                    )}
                    {!billing.atUnitLimit && billing.overageUnits > 0 && (
                        <div style={{ fontSize: '0.75rem', color: PLAN_COLOURS[plan] || '#6b7280', fontWeight: 600, marginTop: '0.125rem' }}>
                            +{billing.overageUnits} over · +£{(billing.overageCost ?? (billing.overageUnits * (billing.overageRate || 0))).toFixed(2)}/mo
                        </div>
                    )}
                </div>
                <div style={{ background: '#f9fafb', borderRadius: '0.5rem', padding: '0.875rem 1rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Properties</div>
                    <div style={{ fontWeight: 700, fontSize: '1.125rem', color: '#111' }}>{billing.propertyCount}</div>
                </div>
                <div style={{ background: '#f9fafb', borderRadius: '0.5rem', padding: '0.875rem 1rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Users</div>
                    <div style={{ fontWeight: 700, fontSize: '1.125rem', color: '#111' }}>
                        {billing.userCount ?? 0} / {billing.userLimit > 999 ? '∞' : (billing.userLimit ?? 1)}
                    </div>
                    {billing.atUserLimit && <div style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600, marginTop: '0.125rem' }}>Limit reached</div>}
                </div>
                {plan !== 'free' && (
                    <div style={{ background: '#f9fafb', borderRadius: '0.5rem', padding: '0.875rem 1rem' }}>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Billing</div>
                        <div style={{ fontWeight: 700, fontSize: '1.125rem', color: '#111', textTransform: 'capitalize' }}>{billing.billingInterval === 'year' ? 'Annual' : 'Monthly'}</div>
                    </div>
                )}
            </div>

            {/* Hard-capped plans (free/solo): no overage exists — the only way past the wall is to upgrade */}
            {HARD_CAPPED_PLANS.includes(plan) && billing.atUnitLimit && isOwner() && (
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#c2410c', marginBottom: '0.25rem' }}>You've reached your {billing.includedUnits}-unit limit</div>
                    <div style={{ fontSize: '0.8125rem', color: '#c2410c', marginBottom: '0.75rem' }}>
                        Upgrade to add more properties or rooms — you only pay for the units your portfolio actually uses.
                    </div>
                    <button onClick={() => setShowUpgrade(true)} style={{ background: '#c2410c', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' }}>Upgrade Plan</button>
                </div>
            )}

            {/* Soft-capped plans (standard/pro): overage bills automatically, nothing to manage here.
                Pro has a volume discount past 90 total units (50p instead of 80p from unit 91 onward, no upper bound) — the
                "at £X/unit" framing only holds when overage is entirely inside one price band, so it's
                dropped once a Pro customer has enough overage units to be spanning both bands, rather
                than showing a flat rate that wouldn't actually match the total figure next to it. */}
            {!HARD_CAPPED_PLANS.includes(plan) && plan !== 'agency' && billing.overageUnits > 0 && (() => {
                const singleBandRate = plan !== 'pro' || billing.overageUnits <= 30;
                return (
                <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#7c3aed', marginBottom: '0.25rem' }}>Extra units billing automatically</div>
                    <div style={{ fontSize: '0.8125rem', color: '#7c3aed' }}>
                        {billing.includedUnits} units included on {billing.planName} · {billing.billableUnits} in use ·
                        {' '}{billing.overageUnits} extra{singleBandRate ? ` at £${(billing.overageRate || 0).toFixed(2)}/unit` : ' (volume discount applied past 90 units)'} ·
                        {' '}<strong>+£{(billing.overageCost ?? (billing.overageUnits * (billing.overageRate || 0))).toFixed(2)}/mo</strong>
                    </div>
                </div>
                );
            })()}

            {/* Agency — managed plan, contact us */}
            {plan === 'agency' && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#166534', marginBottom: '0.25rem' }}>🏢 Agency Plan</div>
                    <div style={{ fontSize: '0.8125rem', color: '#166534', marginBottom: '0.75rem' }}>
                        Your plan is managed directly with casavello. Contact us to adjust your property limit or discuss your requirements.
                    </div>
                    <a href="/contact" style={{ display: 'inline-block', background: '#166534', color: '#fff', borderRadius: '0.5rem', padding: '0.5rem 1rem', fontWeight: 700, fontSize: '0.875rem', textDecoration: 'none' }}>
                        Contact Us →
                    </a>
                </div>
            )}

            <p style={{ fontSize: '0.8125rem', color: '#9ca3af', margin: 0 }}>
                All prices exclude VAT · <a href="/pricing" style={{ color: '#166534' }}>View all plans</a>
            </p>

            {showUpgrade && <UpgradeModal feature="reports" onClose={() => { setShowUpgrade(false); refetch(); }} />}
        </div>
    );
}


// ── BillingSuccess ───────────────────────────────────────────
// Route: /billing/success
export function BillingSuccess() {
    const { refetch, billing } = useBilling();
    const [done, setDone]      = React.useState(false);

    React.useEffect(() => {
        const t = setTimeout(async () => { await refetch(); setDone(true); }, 2500);
        return () => clearTimeout(t);
    }, [refetch]);

    return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0fdf4' }}>
            <div style={{ background: '#fff', borderRadius: '1rem', padding: '3rem 2.5rem', maxWidth: 480, width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#166534', marginBottom: '0.5rem' }}>
                    Welcome to casavello {done && billing?.planName ? billing.planName : ''}!
                </h1>
                <p style={{ color: '#6b7280', marginBottom: '2rem', lineHeight: 1.6 }}>
                    Your subscription is active. All features for your plan are unlocked and ready to use.
                </p>
                {!done && <div style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>Activating your plan...</div>}
                <a href="/" style={{ display: 'inline-block', background: '#166534', color: '#fff', borderRadius: '0.5rem', padding: '0.75rem 1.5rem', fontWeight: 700, textDecoration: 'none', fontSize: '0.9375rem' }}>
                    Go to Dashboard →
                </a>
            </div>
        </div>
    );
}


// ── BillingCancelled ─────────────────────────────────────────
// Route: /billing/cancelled
export function BillingCancelled() {
    return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
            <div style={{ background: '#fff', borderRadius: '1rem', padding: '3rem 2.5rem', maxWidth: 440, width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👋</div>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#111', marginBottom: '0.5rem' }}>No problem</h1>
                <p style={{ color: '#6b7280', marginBottom: '2rem', lineHeight: 1.6 }}>
                    You haven't been charged. Your free account is still active — upgrade whenever you're ready.
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <a href="/pricing" style={{ background: '#166534', color: '#fff', borderRadius: '0.5rem', padding: '0.75rem 1.25rem', fontWeight: 700, textDecoration: 'none', fontSize: '0.9375rem' }}>View Plans</a>
                    <a href="/"        style={{ background: '#f3f4f6', color: '#374151', borderRadius: '0.5rem', padding: '0.75rem 1.25rem', fontWeight: 600, textDecoration: 'none', fontSize: '0.9375rem' }}>Back to Dashboard</a>
                </div>
            </div>
        </div>
    );
}
